import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { projects, tasks } from "../../lib/api";
import { mergeEvents, useTaskStream } from "../../hooks/useTaskStream";
import { NeedsInputBox } from "../chat/NeedsInputBox";
import { SpawnedProposalsCard } from "../kanban/SpawnedProposalsCard";
import { DoneReviewModal } from "../kanban/DoneReviewModal";
import { StoryModal } from "../kanban/StoryModal";
import { useUI } from "../../lib/store";
import { Button } from "../shared/Button";
import { BufferIndicator } from "../shared/BufferIndicator";
import { ActivityTimeline } from "./activity/ActivityTimeline";
import { ChevronDown, Inbox, Pause, Play, Sparkles } from "../shared/Icon";
import { IconButton } from "../shared/IconButton";
import { cn } from "../../lib/cn";
import { formatTokens } from "../../lib/format";
import type { AgentEvent, Task } from "../../lib/types";

/**
 * Activity tab — the structured CLI-fidelity view rendered from `stream-json`
 * envelopes the agent emits. Assistant prose, tool calls (paired with their
 * results), file artifacts, and the run summary render as a grouped timeline
 * instead of a wall of stdout.
 *
 * Data sources:
 *   - `GET /api/tasks/{id}/events`  for the historical backfill
 *   - `agent_event` WS messages for the live tail
 * Merged on `ordinal` so a reload after a long-running task is faithful.
 *
 * Falls back to a helpful empty state when a task ran without
 * `--output-format stream-json`.
 */
export function ActivityViewer() {
  const taskId = useUI((s) => s.selectedTaskId);
  const { data: task, refetch } = useQuery({
    queryKey: ["task", taskId],
    queryFn: () => (taskId ? tasks.get(taskId) : Promise.resolve(null)),
    enabled: Boolean(taskId),
  });
  const { data: hist } = useQuery({
    queryKey: ["task-events", taskId],
    queryFn: () =>
      taskId
        ? tasks.events(taskId)
        : Promise.resolve({ task_id: "", events: [] as AgentEvent[] }),
    enabled: Boolean(taskId),
    refetchOnWindowFocus: false,
  });
  const { data: project } = useQuery({
    queryKey: ["project", task?.project_id],
    queryFn: () =>
      task?.project_id ? projects.get(task.project_id) : Promise.resolve(null),
    enabled: Boolean(task?.project_id),
  });
  const stream = useTaskStream(taskId);

  const events = useMemo(
    () => mergeEvents(hist?.events ?? [], stream.events),
    [hist?.events, stream.events],
  );

  const [autoscroll, setAutoscroll] = useState(true);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const lastEventRef = useRef<HTMLDivElement>(null);
  const [highlightOrdinal, setHighlightOrdinal] = useState<number | null>(null);

  useEffect(() => {
    if (!autoscroll) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [events.length, autoscroll]);

  useEffect(() => {
    if (stream.status) refetch();
  }, [stream.status, refetch]);

  // When the task flips to needs_input, scroll the most recent event into view
  // and flash it — that's the checkpoint the operator must read before
  // deciding how to reply.
  useEffect(() => {
    if (stream.status !== "needs_input") return;
    const last = events[events.length - 1];
    if (!last) return;
    setHighlightOrdinal(last.ordinal);
    lastEventRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = window.setTimeout(() => setHighlightOrdinal(null), 2400);
    return () => window.clearTimeout(t);
  }, [stream.statusChangedAt, stream.status, events]);

  const qc = useQueryClient();
  useEffect(() => {
    if (stream.spawnProposalsBumped > 0) {
      qc.invalidateQueries({ queryKey: ["task", taskId] });
    }
  }, [stream.spawnProposalsBumped, qc, taskId]);

  if (!taskId) {
    return (
      <div className="flex-1 flex items-center justify-center bg-bg-panel">
        <div className="text-center">
          <div className="inline-flex items-center justify-center h-10 w-10 rounded-md bg-bg-inset text-text-quaternary mb-3 mx-auto">
            <Inbox />
          </div>
          <div className="text-small text-text-tertiary">
            Select a task to see its activity.
          </div>
        </div>
      </div>
    );
  }

  const status = stream.status ?? task?.status;
  const running = status === "in_progress" || status === "verifying";

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-bg-panel">
      <ActivityHeader
        taskId={taskId}
        autoscroll={autoscroll}
        onAutoscroll={() => setAutoscroll((v) => !v)}
        running={running}
        tokens={stream.tokensUsed || task?.tokens_used || 0}
        cost={Number(stream.costUsd || task?.cost_usd || 0)}
        cacheRead={stream.cacheReadTokens || task?.cache_read_tokens || 0}
        cacheCreation={
          stream.cacheCreationTokens || task?.cache_creation_tokens || 0
        }
        split={task?.token_split}
        idleSeconds={stream.idleSeconds}
        loop={stream.loop}
        connected={stream.connected}
        eventCount={events.length}
      />

      <div
        ref={scrollerRef}
        onScroll={(e) => {
          const el = e.currentTarget;
          const near = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
          setAutoscroll(near);
        }}
        className="relative flex-1 overflow-y-auto bg-bg-panel"
      >
        {events.length === 0 ? (
          <EmptyActivity
            status={status}
            streamJsonEnabled={
              project?.cli_flags?.includes("--output-format stream-json") ?? false
            }
          />
        ) : (
          <div className="px-3 py-4">
            <ActivityTimeline
              events={events}
              taskKey={taskId}
              highlightOrdinal={highlightOrdinal}
              lastEventRef={lastEventRef}
            />
          </div>
        )}
        {!autoscroll && (
          <IconButton
            icon={<ChevronDown />}
            label="Jump to live"
            onClick={() => {
              setAutoscroll(true);
              const el = scrollerRef.current;
              if (el) el.scrollTop = el.scrollHeight;
            }}
            className="absolute bottom-3 right-4 rounded-full bg-accent text-text-on-accent shadow-md hover:bg-accent-hover"
          />
        )}
      </div>

      {task?.pending_spawn_proposals && task.pending_spawn_proposals.length > 0 && (
        <SpawnedProposalsCard
          taskId={taskId}
          proposals={task.pending_spawn_proposals}
        />
      )}

      {(stream.checkpoint || status === "needs_input") && (
        <NeedsInputBox
          taskId={taskId}
          checkpoint={
            stream.checkpoint ??
            (task?.checkpoint_data
              ? {
                  blocker: String(task.checkpoint_data.blocker ?? ""),
                  options: task.checkpoint_data.options ?? [],
                  completed:
                    (task.checkpoint_data.completed as string[]) ?? [],
                }
              : null)
          }
          onSent={() => refetch()}
        />
      )}
      <ActivityFooter taskId={taskId} status={status} task={task ?? null} />
    </div>
  );
}

// ----- Header -----------------------------------------------------------

interface HeaderProps {
  taskId: string;
  autoscroll: boolean;
  onAutoscroll: () => void;
  running: boolean;
  tokens: number;
  cost: number;
  cacheRead: number;
  cacheCreation: number;
  split: Record<string, number> | undefined;
  idleSeconds: number | null;
  loop: { pattern: string; occurrences: number } | null;
  connected: boolean;
  eventCount: number;
}

const PHASES = ["setup", "work", "wrapup"] as const;

/** Where this task's tokens went: setup / work / wrap-up, as shares. */
function PhaseSplit({ split }: { split: Record<string, number> | undefined }) {
  const total = PHASES.reduce((n, k) => n + (split?.[k] ?? 0), 0);
  if (!split || total === 0) return null;
  const pct = (k: (typeof PHASES)[number]) =>
    `${Math.round(((split[k] ?? 0) / total) * 100)}%`;
  return (
    <span
      className="hidden md:inline-flex items-center gap-1 text-text-quaternary"
      title={PHASES.map((k) => `${k}: ${formatTokens(split[k] ?? 0)}`).join(" · ")}
    >
      {pct("setup")} / {pct("work")} / {pct("wrapup")}
    </span>
  );
}

function StoryButton({ taskId }: { taskId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="The task's story: where it came from, every round, your decisions, the outcome"
        className={cn(
          "h-6 px-2 inline-flex items-center rounded-sm shrink-0 text-caption",
          "text-text-tertiary hover:text-text-primary hover:bg-bg-inset",
          "transition-colors duration-fast ease-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        )}
      >
        Story
      </button>
      {open && <StoryModal taskId={taskId} onClose={() => setOpen(false)} />}
    </>
  );
}

function ActivityHeader(p: HeaderProps) {
  return (
    <div
      className={cn(
        "sticky top-0 z-10 h-9 px-3 flex items-center gap-2 flex-wrap",
        "bg-bg-panel border-b border-border-subtle",
      )}
    >
      <button
        onClick={p.onAutoscroll}
        aria-label={p.autoscroll ? "Pause auto-scroll" : "Resume auto-scroll"}
        title={
          p.autoscroll
            ? "Auto-scrolling — click to pause"
            : "Click to resume auto-scroll"
        }
        className={cn(
          "h-6 w-6 inline-flex items-center justify-center rounded-sm shrink-0",
          "text-text-tertiary hover:text-text-primary hover:bg-bg-inset",
          "transition-colors duration-fast ease-out",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
        )}
      >
        {p.autoscroll ? <Pause /> : <Play />}
      </button>
      <StoryButton taskId={p.taskId} />

      {p.running ? (
        <span className="inline-flex items-center gap-1.5 text-caption text-text-secondary">
          <BufferIndicator size={15} label="Agent working" />
          <span className="text-text-tertiary">{p.eventCount}</span>
        </span>
      ) : (
        <span className="text-caption text-text-tertiary">
          {p.eventCount} {p.eventCount === 1 ? "event" : "events"}
        </span>
      )}

      <div className="flex-1" />
      <div className="flex items-center gap-2 text-caption text-text-tertiary">
        {p.idleSeconds !== null && p.idleSeconds > 0 && (
          <span className="text-status-warning" title="No output for this many seconds">
            idle {p.idleSeconds}s
          </span>
        )}
        {p.loop && (
          <span className="text-status-error" title={p.loop.pattern}>
            loop ×{p.loop.occurrences}
          </span>
        )}
        <span
          className="hidden sm:inline-flex items-center gap-2"
          title="Cost and uncached tokens are the bill. Cache reads and writes are diagnostics — they don't drive what you're charged."
        >
          <span className="text-text-secondary">${p.cost.toFixed(2)}</span>
          <span className="text-text-quaternary">·</span>
          <span>{formatTokens(p.tokens)} uncached</span>
          {(p.cacheRead > 0 || p.cacheCreation > 0) && (
            <>
              <span className="text-text-quaternary">·</span>
              <span className="text-text-quaternary">
                {formatTokens(p.cacheRead)} cache read ·{" "}
                {formatTokens(p.cacheCreation)} cache write
              </span>
            </>
          )}
        </span>
        <PhaseSplit split={p.split} />
        <span
          className={cn(
            "inline-block h-1.5 w-1.5 rounded-full",
            p.connected ? "bg-status-success" : "bg-status-error",
          )}
          title={p.connected ? "Live" : "Disconnected"}
          aria-label={p.connected ? "Live" : "Disconnected"}
        />
      </div>
    </div>
  );
}

function EmptyActivity({
  status,
  streamJsonEnabled,
}: {
  status: string | undefined;
  streamJsonEnabled: boolean;
}) {
  const running =
    status === "in_progress" || status === "verifying" || status === "needs_input";
  return (
    <div className="px-6 py-10 text-center text-small text-text-tertiary">
      <div className="inline-flex items-center justify-center h-10 w-10 rounded-md bg-bg-inset text-text-quaternary mb-3 mx-auto">
        {running ? <BufferIndicator size={20} label="Waiting for the agent" /> : <Sparkles />}
      </div>
      {running ? (
        <p>Waiting for the agent's first structured event…</p>
      ) : streamJsonEnabled ? (
        <p>
          This task ran before live events were captured. Future dispatches on
          this project will populate Activity in real time.
        </p>
      ) : (
        <p>
          No structured events for this task. Add{" "}
          <code className="px-1 py-0.5 rounded-sm bg-bg-inset text-text-secondary font-mono text-[12px]">
            --output-format stream-json --verbose
          </code>{" "}
          to the project's CLI flags to enable rich activity rendering.
        </p>
      )}
    </div>
  );
}

// ----- Footer (dispatch / cancel / mark-done) ---------------------------

interface FooterProps {
  taskId: string;
  status: string | undefined;
  task: Task | null;
}

function ActivityFooter({ taskId, status, task }: FooterProps) {
  const [reviewOpen, setReviewOpen] = useState(false);
  if (
    status !== "todo" &&
    status !== "backlog" &&
    status !== "in_progress" &&
    status !== "needs_input" &&
    status !== "in_review"
  ) {
    return null;
  }
  const dispatch = async () => {
    await tasks.dispatch(taskId);
  };
  const cancel = async () => {
    await tasks.cancel(taskId);
  };
  return (
    <>
      <div className="px-4 py-2 border-t border-border-subtle bg-bg-panel flex items-center gap-2">
        {(status === "todo" || status === "backlog") && (
          <Button variant="primary" size="sm" onClick={dispatch}>
            Dispatch
          </Button>
        )}
        {(status === "in_progress" || status === "needs_input") && (
          <Button variant="secondary" size="sm" onClick={cancel}>
            Cancel
          </Button>
        )}
        {status === "in_review" && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setReviewOpen(true)}
            disabled={!task}
          >
            Mark done
          </Button>
        )}
      </div>
      {reviewOpen && task && (
        <DoneReviewModal task={task} onClose={() => setReviewOpen(false)} />
      )}
    </>
  );
}
